// Persisted set of rune ids the player has learned via in-world books -- gates the
// rune board whenever Developer Mode (settings.ts) is off. Same persistence shape
// as notebook.ts, reset alongside it on New Game.
//
// As of 2026-07-30 (sim extraction step 2) the set itself lives in the shared
// WorldState; the localStorage persistence around it stays here, in `game/`,
// deliberately. Which runes a player knows is world state; *saving it to a browser
// key* is a game concern the simulation should never inherit -- that split is what
// lets the sim be imported under Node (and by the world editor) without touching
// browser APIs. This module still hydrates from localStorage at import time, exactly
// as before, so game behavior is unchanged.
import { getWorld } from './activeWorld';
import { currentRuneId } from '../../rune-lab/src/runes/legacy';

const STORAGE_KEY = 'rune-world-game-learned-runes';

function learned(): Set<string> {
  return getWorld().learnedRunes;
}

function load(): void {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) {
      const set = learned();
      // Through `currentRuneId`, because this key was written by whatever build
      // the player last ran and rune ids have been renamed once (`large` ->
      // `unbound`, 2026-08-14). Reading a stale id straight into the set would
      // silently un-teach a rune the player really did earn: the board gates on
      // the *current* id, so the shelf they read in room16 would simply stop
      // counting, with nothing anywhere saying why.
      for (const id of JSON.parse(raw) as string[]) set.add(currentRuneId(id));
    }
  } catch {
    // Corrupt/unreadable storage -- fall through to nothing learned yet.
  }
}

function save(): void {
  localStorage.setItem(STORAGE_KEY, JSON.stringify([...learned()]));
}

load();

export function hasLearnedRune(id: string): boolean {
  return learned().has(id);
}

export function learnRune(id: string): void {
  learned().add(id);
  save();
}

export function resetLearnedRunes(): void {
  learned().clear();
  save();
}

// Whole-set snapshot/restore for gameSave.ts. This already auto-persists to its own
// localStorage key on every learnRune() -- restoring an older save's set here just
// means overwriting that key back to an earlier point, same as any other undo.
export function getLearnedRuneIds(): string[] {
  return [...learned()];
}

export function setLearnedRuneIds(ids: string[]): void {
  const set = learned();
  set.clear();
  // Migrated for the same reason `load` is, and it is the same class of source:
  // a `SaveData.learnedRunes` written by an older build.
  for (const id of ids) set.add(currentRuneId(id));
  save();
}

/** Re-persists whatever the world's set currently holds, for a caller that
 * changed it without going through the functions above.
 *
 * Exactly one does: undo (see `undoHistory.ts`). It restores a whole
 * `WorldState` at once, `learnedRunes` included, so the world's set moves
 * without this module being told -- and this key is the mirror that would
 * otherwise hand the rune straight back on the next reload. The split this file's
 * header describes is what creates the gap: the set is world state, saving it is
 * a game concern, and anything that writes the first without the second has to
 * close it here. */
export function saveLearnedRunes(): void {
  save();
}
